type BookLevel = readonly [price: bigint, quantity: bigint];

interface BookSnapshot {
  bids: readonly BookLevel[];
  asks: readonly BookLevel[];
}

export const getMidPriceSlippage = (
  isBuy: boolean,
  fillPrice: bigint,
  midPrice: bigint,
): bigint | null => {
  if (midPrice <= 0n) return null;

  return ((isBuy ? 1n : -1n) * (fillPrice - midPrice) * 10n ** 6n) / midPrice;
};

export const getVisibleFill = (levels: readonly BookLevel[], size: bigint) => {
  let remaining = size;
  let visibleCost = 0n;
  let visibleFilled = 0n;
  let projectedBestPrice: bigint | null = null;

  for (const [price, quantity] of levels) {
    if (quantity <= 0n) continue;
    if (remaining === 0n) {
      projectedBestPrice = price;
      break;
    }

    const take = quantity >= remaining ? remaining : quantity;
    visibleCost += price * take;
    visibleFilled += take;
    remaining -= take;

    if (remaining === 0n && take < quantity) {
      projectedBestPrice = price;
      break;
    }
  }

  return { remaining, visibleCost, visibleFilled, projectedBestPrice };
};

// Simulates consuming the taker side of the visible book for `size` and
// returns twice the projected post-order midpoint (kept doubled to avoid
// rounding away half a tick). The first remaining maker level becomes the
// projected taker-side BBO; the opposite side is offset by the original
// snapshot's spread. Null when the visible book cannot absorb the order.
export const getProjectedMidX2 = (
  isBuy: boolean,
  book: BookSnapshot,
  size: bigint,
): bigint | null => {
  const bestAsk = book.asks.find(([, quantity]) => quantity > 0n)?.[0] ?? null;
  const bestBid = book.bids.find(([, quantity]) => quantity > 0n)?.[0] ?? null;
  if (bestAsk === null || bestBid === null) return null;

  const { remaining, projectedBestPrice } = getVisibleFill(
    isBuy ? book.asks : book.bids,
    size,
  );
  if (remaining > 0n || projectedBestPrice === null) return null;

  const spread = bestAsk - bestBid;
  // buy:  projected mid = projectedBestAsk - spread / 2
  // sell: projected mid = projectedBestBid + spread / 2
  const projectedMidX2 = isBuy
    ? 2n * projectedBestPrice - spread
    : 2n * projectedBestPrice + spread;

  return projectedMidX2 > 0n ? projectedMidX2 : null;
};

// Directional slippage of the visible VWAP against the projected post-order
// midpoint, in 1e6 fixed point (10_000n === 1%).
// buy: (vwap - projectedMid) / projectedMid
// sell: (projectedMid - vwap) / projectedMid
export const getProjectedMidSlippage = (
  isBuy: boolean,
  size: bigint,
  visibleCost: bigint,
  visibleFilled: bigint,
  book: BookSnapshot,
): bigint | null => {
  if (size <= 0n || visibleFilled <= 0n) return null;

  const projectedMidX2 = getProjectedMidX2(isBuy, book, size);
  if (projectedMidX2 === null) return null;

  return (
    ((isBuy ? 1n : -1n) *
      (2n * visibleCost - projectedMidX2 * visibleFilled) *
      10n ** 6n) /
    (projectedMidX2 * visibleFilled)
  );
};

// Directional dollar slippage against the projected post-order midpoint:
// size * (vwap - projectedMid) for buys, size * (projectedMid - vwap) for
// sells, in priceDecimals + sizeDecimals fixed point.
export const getProjectedMidSlippageAmount = (
  isBuy: boolean,
  size: bigint,
  visibleCost: bigint,
  visibleFilled: bigint,
  book: BookSnapshot,
): bigint | null => {
  if (size <= 0n || visibleFilled <= 0n) return null;

  const projectedMidX2 = getProjectedMidX2(isBuy, book, size);
  if (projectedMidX2 === null) return null;

  return (
    ((isBuy ? 1n : -1n) *
      size *
      (2n * visibleCost - projectedMidX2 * visibleFilled)) /
    (2n * visibleFilled)
  );
};
